/**
 * protocolCheckCore.js — сверка состава исследования с чек-листом (ядро).
 *
 * Отделено от protocolCheck.js по той же причине, по которой отделён
 * rtProtocolGateCore: сверка — это правила, а правила надо проверять тестами,
 * а не разглядыванием модального окна. Здесь нет ни DOM, ни Cornerstone, ни
 * чтения тегов: на вход приходит готовый разбор серий, на выход — строки
 * вердикта.
 *
 * ПОВОД ДЛЯ ВЫДЕЛЕНИЯ [10.17.0]. В protocolCheck.js слово «исследование» не
 * встречалось ни разу: сверка брала ВСЕ загруженные серии сразу. Пока
 * исследование было одно, это было незаметно. В режиме сравнения их два, и
 * требование «T1 до контраста» может закрыться серией из июльской съёмки, а
 * «T1 после контраста» — из августовской. Протокол отчитается, что всё на
 * месте, хотя ни одно исследование по отдельности не полное. Молча, без
 * единого предупреждения.
 *
 * Такую ошибку нельзя ловить глазами — только тестом, где серии нарочно
 * разложены по разным исследованиям. Ради этого теста ядро и выделено.
 */

export const OK = 'ok';
export const WARN = 'warn';
export const FAIL = 'fail';
export const MANUAL = 'manual';

export const STATUS_ICONS = { ok: '✅', warn: '⚠️', fail: '❌', manual: '🖐' };

/**
 * [10.19.1] Был ли контраст — по тегу и описанию, С УЧЁТОМ ОТРИЦАНИЙ.
 *
 * Прежнее правило `!!agent || /CONTRAST|GD|…/` считало «с контрастом»
 * описания «T1 PRE CONTRAST», «NO CONTRAST», «WITHOUT CONTRAST» и тег
 * ContrastBolusAgent со значением «NONE»: требование «до контраста» падало
 * «серия не найдена», а «после контраста» закрывалось доконтрастной серией —
 * оба вердикта ложные. Правило вынесено сюда, где его проверяет тест.
 *
 * @param {string} agentRaw — тег (0018,0010) ContrastBolusAgent как есть
 * @param {string} desc — описание серии
 */
export function contrastFromTags(agentRaw, desc) {
  const агент = String(agentRaw || '').trim();
  const агентДа = !!агент && !/^(no|none|нет|без|0|-+|n\/a)$/i.test(агент);
  const текст = String(desc || '');
  const да = /\+C|CONTRAST|GD|GADO|МАГНЕВ|ГАДО/i.test(текст);
  const нет = /\b(NO|WITHOUT|NON|PRE|SANS|БЕЗ)[\s_-]*(CONTRAST|КОНТРАСТ)/i.test(текст)
    || /CONTRAST[\s_-]*FREE/i.test(текст);
  return агентДа || (да && !нет);
}

/** Толщина среза против требования профиля. */
export function thicknessStatus(v, limit) {
  if (!Number.isFinite(v)) return FAIL;
  if (v <= limit * 1.05) return OK;      // 1.05 мм при норме 1 — округление тега
  if (v <= limit * 1.3) return WARN;     // близко к норме (напр. BRAVO 1.2 мм)
  return FAIL;
}

/** Интервал между срезами: ноль или перекрытие — хорошо. */
export function gapStatus(g) {
  if (!Number.isFinite(g)) return WARN;  // интервал не определить
  if (g <= 0.05) return OK;              // 0 или перекрытие
  if (g <= 0.15) return WARN;
  return FAIL;
}

export function formatSeries(c) {
  const parts = [`«${String(c.desc || '').slice(0, 34)}»`, `${c.count} срез.`];
  if (Number.isFinite(c.thk)) parts.push(`${c.thk.toFixed(2)} мм`);
  if (Number.isFinite(c.gap)) parts.push(`зазор ${c.gap >= 0 ? '+' : ''}${c.gap.toFixed(2)}`);
  if (!c.isPrimary) parts.push('НЕ ORIGINAL\\PRIMARY');
  return parts.join(' · ');
}

/**
 * Отобрать серии одного исследования.
 *
 * Пустой studyUID означает «все» — так работает обычный режим с одним
 * исследованием, и ломать его незачем. Но как только исследований два,
 * вызывающий ОБЯЗАН сказать, о каком из них спрашивает.
 */
export function seriesOfStudy(list, studyUID) {
  if (!studyUID) return list || [];
  return (list || []).filter((s) => (s.studyUID || s.study || '') === studyUID);
}

/** Проверка одного декларативного требования против списка серий. */
export function checkRequirement(list, req) {
  let cands = (list || []).filter((x) => x.kind === req.kind
    && !x.isLocalizer && !x.isDerived && x.count >= 6);
  if (req.plane) cands = cands.filter((x) => x.plane === req.plane);
  if (req.contrast != null) cands = cands.filter((x) => x.hasContrast === req.contrast);
  if (!cands.length) return { status: FAIL, detail: 'серия не найдена' };

  // [10.19.1] Оцениваем ВСЕХ кандидатов и отвечаем по ЛУЧШЕМУ. Раньше брался
  // самый тонкий: T2 1.0 мм с зазором (FAIL) затмевал полностью годную T2
  // 2.0 мм без зазора, и требование падало при живой годной серии.
  const оценка = (c) => {
    const st = [];
    if (Number.isFinite(req.maxThk)) st.push(thicknessStatus(c.thk, req.maxThk));
    if (req.needGap0) st.push(gapStatus(c.gap));
    if (req.needPrimary) st.push(c.isPrimary ? OK : WARN);
    return st.includes(FAIL) ? FAIL : (st.includes(WARN) ? WARN : OK);
  };
  const ранг = { [OK]: 0, [WARN]: 1, [FAIL]: 2 };
  let лучший = null, статус = null;
  for (const c of [...cands].sort((a, b) => (a.thk || 99) - (b.thk || 99))) {
    const s = оценка(c);
    if (статус == null || ранг[s] < ранг[статус]) { статус = s; лучший = c; }
    if (статус === OK) break;
  }
  return { status: статус, detail: formatSeries(лучший) };
}

/**
 * Полный набор строк вердикта по одному исследованию.
 * @param {Array} list — разобранные серии (analyzeSeries), любых исследований
 * @param {{name: string, reqs: Array}} profile
 * @param {{studyUID?: string}} [o]
 */
export function buildRows(list, profile, o = {}) {
  const свои = seriesOfStudy(list, o.studyUID);
  const reqs = profile?.reqs || [];
  const rows = reqs.map((req) => ({ label: req.label, ...checkRequirement(свои, req) }));

  const nonPrimary = свои.filter((x) => !x.isLocalizer && x.count >= 10 && !x.isPrimary);
  rows.push({
    label: 'Все диагностические серии первичные (ORIGINAL\\PRIMARY)',
    status: nonPrimary.length ? WARN : OK,
    detail: nonPrimary.length
      ? nonPrimary.map((x) => `«${String(x.desc || '').slice(0, 24)}»`).join(', ')
      : 'реконструированных среди серий нет',
  });

  rows.push({
    label: 'Геометрические искажения и артефакты',
    status: MANUAL,
    detail: 'автоматически не определяются — оценивается врачом визуально',
  });

  return { rows, series: свои };
}

/**
 * Свернуть строки в одну короткую оценку — то, что помещается в углу окна.
 *
 * Ручной пункт из счёта исключён намеренно: программа его не проверяла и
 * приписывать себе чужую работу не должна. Он остаётся в полном разборе.
 */
export function summarize(rows) {
  const считаемые = (rows || []).filter((r) => r.status !== MANUAL);
  const всего = считаемые.length;
  const ok = считаемые.filter((r) => r.status === OK).length;
  const warn = считаемые.filter((r) => r.status === WARN).length;
  const fail = считаемые.filter((r) => r.status === FAIL).length;
  const уровень = fail ? FAIL : (warn ? WARN : OK);
  // Замечания называются НАРЯДУ с нехваткой, а не вместо неё. Иначе выходит
  // «0 из 6, не хватает 4» — где ещё два, читателю остаётся гадать, и строка
  // выглядит ошибкой счёта.
  const хвост = [
    fail ? `не хватает ${fail}` : '',
    warn ? `замечаний ${warn}` : '',
  ].filter(Boolean).join(', ');

  return {
    всего, ok, warn, fail, уровень,
    значок: STATUS_ICONS[уровень],
    словами: всего
      ? `Протокол: ${ok} из ${всего}` + (хвост ? ` · ${хвост}` : '')
      : 'Протокол: нечего проверять',
  };
}
